import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { teams } from "@/mocks/teams";

type PlayerState = "idle" | "loading" | "ready" | "unavailable";

const eventIcon: Record<string, { icon: string; className: string }> = {
  goal: { icon: "ri-football-fill", className: "bg-accent-500 text-background-50" },
  yellow: { icon: "ri-bookmark-fill", className: "bg-yellow-400 text-foreground-950" },
  red: { icon: "ri-bookmark-fill", className: "bg-red-500 text-background-50" },
  sub: { icon: "ri-repeat-line", className: "bg-secondary-500 text-background-50" },
  var: { icon: "ri-tv-line", className: "bg-primary-500 text-background-50" },
  kickoff: { icon: "ri-flag-line", className: "bg-background-300 text-foreground-800" },
  half: { icon: "ri-pause-circle-line", className: "bg-background-300 text-foreground-800" },
  end: { icon: "ri-stop-circle-line", className: "bg-background-300 text-foreground-800" },
};

export default function LiveWatch() {
  const { matchId } = useParams<{ matchId: string }>();
  const match = matches.find((m) => m.id === matchId);

  const [playerState, setPlayerState] = useState<PlayerState>("idle");
  const [activeStage, setActiveStage] = useState<string>("");

  useEffect(() => {
    setPlayerState(match && match.status === "live" ? "idle" : "unavailable");
    setActiveStage(match?.stage ?? "");
  }, [matchId, match]);

  const stages = useMemo(() => {
    const unique = Array.from(new Set(matches.map((m) => m.stage)));
    return unique;
  }, []);

  const related = useMemo(() => {
    if (!match) return [];
    return matches
      .filter((m) => m.id !== match.id && m.stage === activeStage)
      .slice(0, 4);
  }, [match, activeStage]);

  const startPlay = () => {
    if (!match || match.status !== "live") return;
    setPlayerState("loading");
    window.setTimeout(() => setPlayerState("ready"), 1600);
  };

  if (!match) {
    return (
      <div className="w-full px-4 md:px-6 pt-32 pb-20 min-h-[70vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          未找到该场比赛
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          这场比赛可能已结束或链接有误，请返回赛程页重新选择。
        </p>
        <Link
          to="/schedule/2025-26"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
        >
          返回赛程
        </Link>
      </div>
    );
  }

  const home = teams.find((t) => t.id === match.homeTeamId);
  const away = teams.find((t) => t.id === match.awayTeamId);
  const progress = match.minute ? Math.min(100, Math.round((match.minute / 90) * 100)) : 0;
  const possession = match.stats?.possession ?? [50, 50];

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-8 md:pb-10">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <Link to={`/schedule/${match.season}`} className="hover:text-accent-400 transition-colors">
              赛程
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <span className="text-background-100">直播观看</span>
          </nav>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse-live"></span>
              {match.status === "live" ? `直播中 · 第 ${match.minute} 分钟` : "非直播时段"}
            </span>
            <span className="text-xs text-background-200">
              {match.stage} · {match.round} · {match.venue}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50">
            {home?.name} vs {away?.name}
          </h1>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8 flex flex-col gap-6">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950 border border-background-200">
              <img
                src={match.cover}
                alt={`${home?.name} vs ${away?.name} 直播画面演示`}
                title={`${home?.name} vs ${away?.name} 欧冠直播`}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
                  playerState === "ready" ? "opacity-40" : "opacity-25"
                }`}
              />
              <div className="absolute inset-0 bg-primary-950/60"></div>

              <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6">
                {playerState === "idle" && (
                  <>
                    <button
                      type="button"
                      onClick={startPlay}
                      aria-label="开始播放演示画面"
                      className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-3xl md:text-4xl hover:bg-accent-600 transition-colors"
                    >
                      <i className="ri-play-fill" aria-hidden="true"></i>
                    </button>
                    <p className="mt-4 text-sm md:text-base text-background-100 font-medium">
                      点击播放，加载直播演示画面
                    </p>
                    <p className="mt-1 text-xs text-background-300">
                      当前为前端演示播放器，真实直播信号需接入第三方直播源
                    </p>
                  </>
                )}

                {playerState === "loading" && (
                  <>
                    <span className="w-12 h-12 rounded-full border-2 border-background-300 border-t-accent-500 animate-spin"></span>
                    <p className="mt-4 text-sm text-background-100">正在连接直播信号…</p>
                    <p className="mt-1 text-xs text-background-300">演示加载状态</p>
                  </>
                )}

                {playerState === "ready" && (
                  <>
                    <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                      <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse-live"></span>
                      演示画面
                    </span>
                    <p className="mt-4 text-sm md:text-base text-background-100 font-medium">
                      直播画面演示区域
                    </p>
                    <p className="mt-1 max-w-md text-xs text-background-300">
                      此为前端演示内容，不包含真实视频流。接入第三方直播接口后即可播放实时画面。
                    </p>
                  </>
                )}

                {playerState === "unavailable" && (
                  <>
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10 text-3xl text-background-200">
                      <i className="ri-signal-wifi-off-line" aria-hidden="true"></i>
                    </span>
                    <p className="mt-4 text-sm md:text-base text-background-100 font-medium">
                      直播尚未开始或视频信号暂未接入
                    </p>
                    <p className="mt-1 text-xs text-background-300">
                      比赛开始后请返回本页刷新，或查看下方赛程信息
                    </p>
                  </>
                )}
              </div>

              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-background-950/70 px-2.5 py-1 text-[11px] text-background-100">
                <i className="ri-information-line" aria-hidden="true"></i>
                演示播放器
              </span>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                  实时比分
                </h2>
                <span className="text-xs text-foreground-500">
                  {match.status === "live" ? `第 ${match.minute} 分钟` : "比赛未开始"}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 flex flex-col items-center gap-2 text-center">
                  <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={home?.crest}
                      alt={`${home?.name}队徽`}
                      title={`${home?.name} 欧冠直播`}
                      className="w-full h-full object-contain"
                    />
                  </span>
                  <span className="text-sm font-medium text-foreground-900">{home?.name}</span>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                    {match.homeScore ?? "-"} : {match.awayScore ?? "-"}
                  </div>
                </div>
                <div className="flex-1 flex flex-col items-center gap-2 text-center">
                  <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={away?.crest}
                      alt={`${away?.name}队徽`}
                      title={`${away?.name} 欧冠直播`}
                      className="w-full h-full object-contain"
                    />
                  </span>
                  <span className="text-sm font-medium text-foreground-900">{away?.name}</span>
                </div>
              </div>

              <div className="mt-6">
                <div className="h-2 w-full rounded-full bg-background-200 overflow-hidden">
                  <div
                    className="h-full bg-accent-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="mt-2 flex justify-between text-xs text-foreground-500">
                  <span>开场</span>
                  <span>半场</span>
                  <span>全场</span>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-xs text-foreground-600 mb-2">
                  <span>控球率 {possession[0]}%</span>
                  <span>{possession[1]}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-background-200 overflow-hidden flex">
                  <div className="h-full bg-primary-500" style={{ width: `${possession[0]}%` }}></div>
                  <div className="h-full bg-secondary-500" style={{ width: `${possession[1]}%` }}></div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                赛事实况
              </h2>
              {match.events.length === 0 ? (
                <div className="rounded-md border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
                  比赛尚未开始，暂无实况事件
                </div>
              ) : (
                <ol className="relative">
                  {match.events
                    .slice()
                    .sort((a, b) => b.minute - a.minute)
                    .map((e, i) => {
                      const meta = eventIcon[e.type] ?? eventIcon.kickoff;
                      return (
                        <li key={`${e.minute}-${i}`} className="flex gap-4 pb-5 last:pb-0">
                          <span
                            className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-sm ${meta.className}`}
                          >
                            <i className={meta.icon} aria-hidden="true"></i>
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-heading text-sm font-bold text-foreground-950">
                                {e.minute}&apos;
                              </span>
                              <span className="text-sm font-semibold text-foreground-900">
                                {e.player}
                              </span>
                              {e.team !== "none" && (
                                <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600">
                                  {e.team === "home" ? home?.name : away?.name}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                              {e.detail}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                </ol>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-3">
                比赛阶段
              </h3>
              <div className="flex flex-wrap gap-2">
                {stages.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setActiveStage(s)}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                      s === activeStage
                        ? "bg-secondary-100 text-secondary-900 ring-1 ring-secondary-300"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
                切换阶段以查看该阶段的相关比赛，实况信息会随所选比赛更新。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                  相关比赛
                </h3>
                <Link
                  to={`/schedule/${match.season}`}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  更多
                </Link>
              </div>
              {related.length === 0 ? (
                <p className="text-sm text-foreground-500 py-4 text-center">该阶段暂无其他比赛</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {related.map((m) => {
                    const h = teams.find((t) => t.id === m.homeTeamId);
                    const a = teams.find((t) => t.id === m.awayTeamId);
                    return (
                      <Link
                        key={m.id}
                        to={`/detail/${m.id}`}
                        className="group flex items-center gap-3 rounded-md border border-background-200 p-3 hover:border-primary-300 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate">
                            {h?.name} vs {a?.name}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            {m.date.slice(5).replace("-", "/")} {m.time}
                          </p>
                        </div>
                        <span className="shrink-0 font-heading text-sm font-bold text-foreground-950">
                          {m.homeScore !== null ? `${m.homeScore}:${m.awayScore}` : "VS"}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                观赛提示
              </h3>
              <ul className="space-y-2 text-xs text-foreground-600 leading-relaxed">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" aria-hidden="true"></i>
                  本页播放器为前端演示区域，不提供真实视频流。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" aria-hidden="true"></i>
                  实时比分与实况事件为演示数据，仅供界面效果参考。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" aria-hidden="true"></i>
                  接入第三方直播接口后即可展示真实画面。
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}